import { useEffect, useState } from 'react'

/**
 * The value, settled — it lags `value` until typing stops for `delayMs`.
 *
 * The ticket search is a server-side filter now, so the raw input value cannot
 * drive the query: every keystroke would be a network round trip, and each one
 * changes the Apollo cache key, so the list would flicker through a partial
 * term's results on the way to the real one.
 *
 * The INPUT stays bound to the raw value — the field must never lag what was
 * typed. Only the query reads the debounced one.
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs)
    // Clearing on every change is what makes this a debounce rather than a
    // throttle: a keystroke inside the window restarts the wait instead of
    // letting the previous timer fire with a stale value.
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return settled
}
